/**
 * Campaigns Tab Component
 * Shows campaign ROI metrics and performance table
 */

export default function CampaignsTab({ 
  campaignROI,
  formatCurrency,
  formatPercent
}) {
  if (!campaignROI) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground">
        No campaign data available
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Total Signups</p>
          <p className="text-2xl font-bold text-foreground">
            {campaignROI.summary?.total_signups || 0}
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Codes Redeemed</p>
          <p className="text-2xl font-bold text-foreground">
            {campaignROI.summary?.total_codes_redeemed || 0}
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Conversion Rate</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {formatPercent(campaignROI.summary?.overall_conversion_rate)}
          </p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border">
          <p className="text-sm text-muted-foreground">Campaign Revenue</p>
          <p className="text-2xl font-bold text-foreground">
            {formatCurrency(campaignROI.summary?.total_campaign_revenue)}
          </p>
        </div>
      </div>

      {/* Campaign Table */}
      <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
        <h3 className="font-medium text-foreground mb-4">Campaign Performance</h3>
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-2 font-medium text-muted-foreground">Campaign</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Signups</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Redeemed</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Conversion</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Orders</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">Revenue</th>
                <th className="text-right py-3 px-2 font-medium text-muted-foreground">AOV</th>
              </tr>
            </thead>
            <tbody>
              {campaignROI.campaigns?.map((campaign) => (
                <tr 
                  key={campaign.campaign_id} 
                  className="border-b border-border/50 hover:bg-muted/30 dark:hover:bg-muted/20"
                >
                  <td className="py-3 px-2">
                    <div>
                      <p className="font-medium text-foreground">{campaign.campaign_name}</p>
                      <p className="text-xs text-muted-foreground">{campaign.discount_percent}% discount</p>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right text-foreground">{campaign.total_signups}</td>
                  <td className="py-3 px-2 text-right text-foreground">{campaign.codes_redeemed}</td>
                  <td className="py-3 px-2 text-right">
                    <span className={campaign.conversion_rate > 20 ? 'text-green-600 dark:text-green-400' : 'text-foreground'}>
                      {formatPercent(campaign.conversion_rate)}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right text-foreground">{campaign.orders_generated}</td>
                  <td className="py-3 px-2 text-right font-medium text-foreground">
                    {formatCurrency(campaign.revenue_generated)}
                  </td>
                  <td className="py-3 px-2 text-right text-foreground">
                    {formatCurrency(campaign.avg_order_value)}
                  </td>
                </tr>
              ))}
              {!campaignROI.campaigns?.length && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-muted-foreground">
                    No campaign data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
